import * as React from 'react';
import { Minus, Plus, Tilde } from '@phosphor-icons/react';
import {
  activityLogBlockRecipe,
  activityLogListRecipe,
} from '../recipes/activity-log.recipe';
import {
  activityInFilter,
  type ActivityLogChange,
  type ActivityLogEntry,
  type ActivityLogFilter,
} from './ActivityLogView';
import { Avatar } from './Avatar';
import { EmptyState } from './EmptyState';
import { Icon } from './Icon';
import { Divider, Inline, Stack } from './Layout';
import { Loading } from './Spinner';
import { Text } from './Text';
import { ToggleGroup, ToggleGroupItem } from './ToggleGroup';

export interface ActivityLogListProps {
  /** Every event with something to show, newest first. */
  entries: ActivityLogEntry[];
  loading?: boolean;
}

const FILTERS: { key: ActivityLogFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'driver', label: 'Driver' },
  { key: 'dispatch', label: 'Dispatch' },
];

/** The same marks DiffLine draws in its margin, as the phone's glyphs. */
const GLYPH = {
  add: { icon: Plus, tone: 'success' },
  remove: { icon: Minus, tone: 'destructive' },
  change: { icon: Tilde, tone: 'info' },
} as const;

/** A line that is one phrase ("1 Image") is named by what happened to it. */
const FIELD_FOR_PHRASE = {
  add: 'Added',
  remove: 'Removed',
  change: 'Changed',
} as const;

function LogItem({ change }: { change: ActivityLogChange }) {
  const glyph = GLYPH[change.kind];
  return (
    <Inline gap={2} align="center">
      <Icon icon={glyph.icon} size="md" tone={glyph.tone} />
      <Inline gap={2} align="baseline" wrap>
        <Text as="span" variant="body-strong">
          {change.field ?? FIELD_FOR_PHRASE[change.kind]}
        </Text>
        <Text as="span" variant="body-strong" tone="muted">
          {change.from !== undefined
            ? `${change.from} → ${change.value}`
            : change.value}
        </Text>
      </Inline>
    </Inline>
  );
}

/**
 * A call's activity as the phone's Call activity sheet draws it (Figma
 * 577:140196): All, Driver and Dispatch in a segmented control over a
 * running total, then each event between hairline rules, its kind and
 * date, who made it and when, and a glyph per change: a green plus for
 * something added, a red minus for something taken away.
 *
 *   <BottomSheetContent title="Call activity">
 *     <ActivityLogList entries={entries} loading={isLoading} />
 *   </BottomSheetContent>
 *
 * The desk's log is ActivityLogView, a card with counts on its filter and
 * paging; this is the same entries and the same filter (activityInFilter)
 * at a thumb's sizes, everything at once. Drawn in a BottomSheet's body,
 * whose 24px inset its rules run through to the sheet's edges.
 */
export function ActivityLogList({
  entries,
  loading = false,
}: ActivityLogListProps) {
  const [filter, setFilter] = React.useState<ActivityLogFilter>('all');

  if (loading) return <Loading size="lg" pad="md" label="Loading activity" />;
  if (entries.length === 0) return <EmptyState title="No activity yet" />;

  const shown = activityInFilter(entries, filter);

  return (
    <div className={activityLogListRecipe()}>
      <div className={activityLogBlockRecipe()}>
        <ToggleGroup
          type="single"
          value={filter}
          // Radix clears the value when the on item is pressed again; one
          // filter is always in effect, so ignore that.
          onValueChange={(value) => {
            if (value) setFilter(value as ActivityLogFilter);
          }}
          bordered={false}
          gap={0}
          shape="segmented"
          variant="segment"
          size="segment"
          aria-label="Filter activity"
        >
          {FILTERS.map((f) => (
            <ToggleGroupItem key={f.key} value={f.key}>
              {f.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Inline gap={2} align="center" justify="between">
          <Text as="span" variant="body-lg-strong">
            Activity Total
          </Text>
          <Text as="span" variant="body-lg-strong" tone="muted" numeric>
            {shown.length}
          </Text>
        </Inline>
      </div>

      {shown.map((entry) => (
        <React.Fragment key={entry.id}>
          <Divider />
          <div className={activityLogBlockRecipe()}>
            <Inline gap={3} align="baseline" justify="between">
              <Text as="span" variant="body-lg-strong">
                {entry.action}
              </Text>
              <Text as="span" variant="body-lg-strong" tone="muted" numeric>
                {/* With nobody to name there is no row for the time, so it
                    joins the date. */}
                {entry.actor ? entry.date : `${entry.date} · ${entry.time}`}
              </Text>
            </Inline>
            <Stack gap={2}>
              {entry.actor ? (
                <Inline gap={3} align="center" justify="between">
                  <Inline gap={2} align="center">
                    <Avatar size="2xs" name={entry.actor} />
                    <Text as="span" variant="body-strong">
                      {entry.actor}
                    </Text>
                  </Inline>
                  <Text as="span" variant="body-strong" tone="muted" numeric>
                    {entry.time}
                  </Text>
                </Inline>
              ) : null}
              {entry.changes.map((change, i) => (
                <LogItem
                  key={`${change.field ?? ''}:${change.value}:${i}`}
                  change={change}
                />
              ))}
            </Stack>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
